"use client"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog"
import { X } from "lucide-react"
import Cookies from "js-cookie"
import { useState, useEffect } from "react"
import { getFlag, setFlag } from "@/lib/api/flags"
import { Blog, Note } from "@types"
import { COINS_ENABLED } from "@/lib/utils/flags"

const READER_COIN_ONBOARDING_SHOWN = "readerCoinOnboardingShown"
const READER_ONBOARDING_MODAL_SHOWN_FLAG = "readerOnboardingModalShown"

type Props = {
  blog?: Blog
  post?: Note
  isLoggedIn: boolean
}

/**  Coin onboarding modal for readers shown on the post page one time (then sets a cookie to not be shown again) */
export function ReaderCoinOnboardingModal({ blog, post, isLoggedIn }: Props) {
  const [isOpen, setIsOpen] = useState(false)

  useEffect(() => {
    const shouldShow = async () => {
      // Only show for coined posts on non-custom domains
      if (
        !COINS_ENABLED ||
        (post && blog && (!post.coinId || blog.custom_domain))
      )
        return

      // Get cookie value
      const hasBeenShownCookie = Cookies.get(READER_COIN_ONBOARDING_SHOWN)

      // If logged in, check if the flag has been set
      if (isLoggedIn) {
        const hasBeenShownFlag = await getFlag(
          READER_ONBOARDING_MODAL_SHOWN_FLAG
        )

        // If the flag is not set and the cookie not set, show the modal and update the values
        if (!hasBeenShownFlag) {
          if (!hasBeenShownCookie) setIsOpen(true)
          await setFlag(READER_ONBOARDING_MODAL_SHOWN_FLAG, true)
          Cookies.set(READER_COIN_ONBOARDING_SHOWN, "true")
        }
      } else {
        if (!hasBeenShownCookie) setIsOpen(true)
      }
    }
    shouldShow()
  }, [post, isLoggedIn, blog])

  const handleDismiss = async () => {
    setIsOpen(false)
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleDismiss()}>
      <DialogContent className="sm:max-w-[462px]">
        <button
          onClick={handleDismiss}
          className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none"
        >
          <X className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </button>
        <DialogHeader>
          <DialogTitle className="text-lg font-normal leading-[18px] tracking-[-0.025em]">
            Support ideas that move you
          </DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground space-y-3">
            <div className="mb-2">
              Buying coins is the main way readers show appreciation for great
              writing on Paragraph.
            </div>
            <div className="flex items-start gap-2">
              <span>❤️</span>
              <div>
                <div className="font-medium text-foreground">
                  Lift up writers and amplify their ideas.
                </div>
                <div>
                  Coins let readers back the ideas they care about and help
                  those posts reach more people.
                </div>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <span>🌟</span>
              <div>
                <div className="font-medium text-foreground">
                  Show you were an early supporter.
                </div>
                <div>Coins are a lasting signal that you showed up first.</div>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <span>🌱</span>
              <div>
                <div className="font-medium text-foreground">
                  Help writers grow.
                </div>
                <div>
                  Your contribution helps writers earn, reach more readers, and
                  grow the value of their coins over time.
                </div>
              </div>
            </div>
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button className="w-full" onClick={handleDismiss}>
            Got it
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
